import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Progress } from "@/ui/components/ui/progress";
import { Button } from "@/ui/components/ui/button";
import {
  Loader2,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Download,
  Share2,
  Maximize,
  ChevronLeft,
  ChevronRight,
  Repeat,
  CircleSlash,
  Link,
  Unlink,
  Crosshair,
  RectangleHorizontal,
  RectangleVertical,
} from "lucide-react";
import { useTimelineContext } from "@/machines/context/timeline-context";
import { useCursorStore } from "@/machines/context/cursor-store.ts";
import { useVideoData, useVideoPlayback, useVideoKeyboardShortcuts } from "./hooks";
import { computeSeekPosition, computeAbsoluteTimestamp } from "./utils/video-sync";
import { formatOrgTime } from "@/shared/utils/date/org-time";
import { useVideoLibraryStore } from "@/machines/stores/useVideoLibraryStore";
import { useLiveDelayStore } from "@/map/stores/useLiveDelayStore";
import {
  liveBufferMs,
  liveClipIndex,
  nextLiveClipIndex,
  shouldRealignToBuffer,
} from "@/machines/utils/live-playback-buffer";
import { VideoScrubber } from "./video-scrubber";
import { cn } from "@/ui/lib/utils";

export type VideoOrientation = "vertical" | "horizontal";

// Skip re-seeking when the element is already within this many seconds of the
// target. Prevents sub-second cursor jitter (and re-fires of the same seek) from
// thrashing the media buffer mid-playback.
const SEEK_TOLERANCE_SECONDS = 0.35;

// Live map primary only: never seek to within this of a clip's end. Seeking onto
// (or past) the very end stalls the element at 'ended' with the cursor unable to
// advance — the deadlock. Staying this far back leaves frames to play, so the RAF
// loop advances the cursor forward onto real footage on its own.
const LIVE_SEEK_END_GUARD_MS = 750;

// Live map primary only: a stall (`waiting`/`stalled`, NOT `ended`) this close to
// the current clip's declared end — while on the newest loaded clip — reads as
// "caught up to the live edge, waiting for the next clip to upload" rather than a
// mid-clip network hiccup. It arms resume-on-arrival (same as `handleVideoEnded`
// does on a clean end). Generous enough to catch a stall a couple seconds short of
// the declared end (a still-finalizing clip whose buffered media runs out before
// its metadata duration); far below the ~30s clip length so a genuine mid-clip
// hiccup (playhead nowhere near the end) never trips it.
const LIVE_STALL_END_TOL_SECONDS = 3;

// How long to wait before resuming a stop the operator did not make. Long enough
// that a rollover's own pause/play pair settles first, far short of the map's
// leave-live debounce so an automatic stop never counts as leaving live.
const LIVE_RESUME_DELAY_MS = 250;

// Live map primary only: how often to re-check that a tile which should be
// playing actually is.
//
// The pause-driven resume above cannot cover a rollover. The clip change that
// follows `ended` re-runs that effect, whose cleanup clears the pending resume;
// its own `play()` 100ms later races the new source's load and is rejected with
// AbortError; and the element is now paused at 0 of the new clip, so no further
// `pause` event will ever fire to arm anything again. Measured after a Go-live:
// the primary sat at 0.03 of a 30s clip at readyState 4 — fully loaded, simply
// never asked to play — and because the primary's RAF loop IS the shared cursor,
// the whole view froze with it.
//
// A poll rather than an event, precisely because the failure is the ABSENCE of
// any further event.
const LIVE_RESUME_WATCHDOG_MS = 1000;

// How long the shared cursor may stay silent while the primary is playing
// before its rAF loop is presumed dead and re-armed. The loop writes every
// ~33ms, so a second of silence is already far outside normal; anything shorter
// would re-arm during an ordinary seek cooldown for no reason.
const CURSOR_STALL_MS = 1000;

// Followers only: floor on how often a cursor-driven reposition may sample the
// element's position into React state for the paused scrubber. Caps the worst
// case (a follower that never keeps sync, so EVERY cursor tick repositions it)
// at 5 re-renders/sec — no worse than the `timeupdate` handler followers
// deliberately don't have — while the normal case stays at zero.
const FOLLOWER_SAMPLE_THROTTLE_MS = 200;

// Followers only: a cursor move larger than this is a SCRUB (someone moved the
// playhead), not the primary's playback advancing. Playback writes land every
// ~33ms and carry at most ~100ms of media even at 3x, so anything past this is a
// jump rather than the clock ticking.
const FOLLOWER_SCRUB_JUMP_MS = 400;

// Followers only: how often a follower that SHOULD be playing but is paused may
// still be dragged onto the cursor.
//
// The drift seek used to be unconditional, and on a follower that never started
// it fired twice a second forever: the cursor advances ~0.5s of media every 0.5s
// of wall clock, so the 0.5s tolerance is crossed again immediately after every
// correction. Measured on the map, 46 seeks in 24s on a tile that was paused for
// 100% of the window — which is both the "it jumps every second instead of
// playing" the operator sees AND the reason it can never recover, since each
// seek flushes the decoder before it can buffer. Retrying play (below) is the
// actual fix; this only keeps a durably stuck tile from drifting arbitrarily far
// behind the cursor in the meantime.
const FOLLOWER_STUCK_RESEEK_MS = 3000;

// Followers only: how often to retry play() on a tile that should be playing.
const FOLLOWER_RESUME_RETRY_MS = 1000;

// `HTMLMediaElement.HAVE_CURRENT_DATA` — there is a frame at the current
// position. Below this there is nothing to start.
const HAVE_CURRENT_DATA = 2;

interface VideoPreviewEnhancedProps {
  device_mxid: string;
  orientation?: VideoOrientation;
  onOrientationChange?: (orientation: VideoOrientation) => void;
  followerMode?: boolean;
  /**
   * Map-only "keep the primary playing while live-following": mutes this element
   * (so the browser permits autoplay without a user gesture) and retries play as
   * soon as the media is ready and whenever the flag re-arms / footage switches.
   * Also switches this tile into footage-clamped live mode (never blanks ahead of
   * footage, polls for new clips, auto-advances into arriving clips, rolls
   * continuously). Defaults false, so the timeline editor and machine-detail are
   * unchanged (unmuted, manual play, no clamp). Only meaningful on the primary
   * (`!followerMode`).
   */
  liveAutoplay?: boolean;
  /**
   * Map-only: reports the newest available footage edge (start/end ms of the last
   * clip) whenever it changes while live, so the page can pin the shared cursor to
   * real footage. Reports null when not live / no footage. Wired for the primary
   * tile only; undefined (a no-op) everywhere else.
   */
  onFootageEdgeChange?: (edge: { startMs: number; endMs: number } | null) => void;
  /**
   * Map-only: a "Go live" jump target (ms), set anew on each Go-live click. Makes
   * the primary drop the stale alert-era footage and re-fetch around the live edge
   * so it lands on the newest clip instead of crawling from the alert. Primary
   * only; undefined elsewhere.
   */
  jumpToLiveTime?: number | null;
  /**
   * Map-only: strip the icon toolbar (timeline-sync, autoplay, center,
   * orientation, download, share) and move fullscreen onto the picture, so the
   * bar stays a thin line rather than a panel competing with the video.
   *
   * The TRANSPORT (play/pause, skip, speed) survives on the PRIMARY tile — the
   * bottom one, which owns the clip sequence and drives the shared playback, so
   * an operator needs to be able to stop and step it. Follower tiles are extra
   * angles of that same playback: a second transport there would be a competing
   * way to move one timeline, so they keep the scrubber alone.
   *
   * Off elsewhere, so the editor and machine-detail keep the full bar.
   */
  minimalControls?: boolean;
  /**
   * Map-only: report that this tile must be released from live-follow. Fired when
   * the user puts THIS element into native fullscreen — the browser's own controls
   * appear there, and live-follow (retry-play, the catch-up jump, resume-on-arrival)
   * would fight every scrub or pause the user makes with them. Routed to the page's
   * existing leave-live handler, the ONE source of truth for "are we following
   * live", so the Live chip / "Go live" button stay honest. Undefined (a no-op) in
   * the timeline editor and machine-detail, which never follow live.
   */
  onLeaveLive?: () => void;
  /**
   * The MAP is live-following (panel-level, true for every tile in the stack).
   * Distinct from `liveAutoplay`, which is the per-tile "you drive live
   * playback" flag and is set on the primary alone. A follower needs to know
   * the view is live so it can roll to the next clip on its own.
   */
  liveFollowing?: boolean;
  /**
   * Render the controls bar on FOLLOWER tiles too, wired as a second remote for
   * the ONE shared playback rather than as an independent player: play/pause and
   * speed go through the timeline context, seeking through the shared cursor, and
   * the primary's existing mirror effects carry every tile. Fullscreen stays
   * genuinely per-tile. Opt-in (`/map`'s per-machine stack) so the follower tiles
   * that are extra camera ANGLES — `SecondaryCamera`, used on machine-detail —
   * keep their bar-less look untouched.
   */
  sharedTransport?: boolean;
}

const VideoPreviewEnhanced = ({ device_mxid, orientation = "vertical", onOrientationChange, followerMode = false, liveAutoplay = false, onFootageEdgeChange, jumpToLiveTime, minimalControls = false, sharedTransport = false, onLeaveLive, liveFollowing = false }: VideoPreviewEnhancedProps) => {
  const { t } = useTranslation("machines");
  const {
    setSelectedTime,
    isVideoPlaying: primaryIsPlaying,
    setIsVideoPlaying,
    setIsVideoLinked,
    videoPlaybackRate: primaryPlaybackRate,
    setVideoPlaybackRate,
    requestCenter,
    playRequest,
    requestPlay,
    pauseRequest,
    requestPause,
  } = useTimelineContext();
  // Read the cursor from the store, never as a render value: this tile must NOT
  // re-render on the ~30×/sec live-playback cursor writes. All cursor reads below
  // go through `cursorStore` (effects / callbacks), so the primary AND followers
  // stay quiet on ticks.
  const cursorStore = useCursorStore();
  const [continuousPlayback, setContinuousPlayback] = useState(false);
  const [syncWithTimeline, setSyncWithTimeline] = useState(true);

  // Map live-follow, primary tile only. Drives the footage-clamped data path,
  // continuous rollover, and auto-advance into newly-arrived clips. Never true
  // for followers or in the editor / machine-detail (they don't pass liveAutoplay).
  const liveActive = liveAutoplay && !followerMode;

  // Operator-chosen floor for the live buffer (map Settings). Read as state so a
  // change takes effect on the next rollover without a remount.
  const liveDelayMs = useLiveDelayStore((s) => s.liveDelayMs);

  // Where a deliberate "Go live" should land: behind the wall clock by whatever
  // this camera's pipeline has needed lately.
  //
  // Resolved ONCE per click and then held. `useVideoData` treats any change in
  // `jumpToLiveTime` as a fresh Go-live — it drops every accumulated clip and
  // refetches — and the buffer estimate moves each time a clip arrives. Computed
  // per render, that made every poll look like another Go-live: the tile wiped
  // its footage, restarted, and sat at 0:00 forever while the availability strip
  // showed a full row of clips.
  const bufferedJumpRef = useRef<{ raw: number | null; value: number | null }>({
    raw: null,
    value: null,
  });
  if (jumpToLiveTime !== bufferedJumpRef.current.raw) {
    bufferedJumpRef.current = {
      raw: jumpToLiveTime ?? null,
      value:
        jumpToLiveTime == null
          ? null
          : jumpToLiveTime - liveBufferMs(useVideoLibraryStore.getState().getClipArrivals(device_mxid), liveDelayMs),
    };
  }
  const bufferedJumpToLiveTime = bufferedJumpRef.current.value;

  // `followerMode` flips WITHOUT a remount (the map stack swaps which tile is
  // active), so the mount-stable cursor subscriptions must read it from a ref
  // rather than close over the render value.
  const followerModeRef = useRef(followerMode);
  followerModeRef.current = followerMode;

  // Set when the primary reached the end of the newest clip while live (caught up
  // to the live edge). A newly-arrived clip then advances playback into it.
  const waitingForFootageRef = useRef(false);

  // Did the OPERATOR stop this element, or did it stop on its own?
  //
  // A `pause` event says nothing about why. Live playback stops for reasons
  // nobody asked for — a stall, a source switch on rollover, the end of the
  // clips that exist so far — and treating those like a deliberate stop leaves
  // the operator staring at a frozen live view they never paused. Only an
  // explicit stop sets this; everything else is resumed.
  const userPausedRef = useRef(false);

  // True while THIS element is in native fullscreen, where the browser's own
  // controls are the transport. Everything that would move this element on the
  // stack's behalf checks it, so a scrub / pause made with the native controls
  // isn't immediately overridden. A ref (not state): it's read from imperative
  // effects and a mount-stable cursor subscription, and it must be up to date at
  // the instant `fullscreenchange` fires — before any re-render.
  const isFullscreenRef = useRef(false);

  // Ref to track last update time to throttle timeline updates
  const lastTimelineUpdateRef = useRef<number>(0);
  const rafIdRef = useRef<number | null>(null);

  // Track last selectedTime to detect user clicks (jumps > 1s vs playback sync ~33ms)
  const lastSelectedTimeRef = useRef<number | null>(null);

  // Cooldown after user-initiated seek to prevent playback sync from overriding
  const seekCooldownRef = useRef<number>(0);

  // Pending seek: persists across video switches so the seek always applies
  const pendingSeekTimeRef = useRef<number | null>(null);

  // Track whether seek was user-initiated (timeline click) vs auto-transition (video ended)
  const isUserSeekRef = useRef<boolean>(false);

  // Set right before the RAF playback loop writes the cursor. The seek detector
  // uses it to distinguish the video's OWN playback updates (must never reseek —
  // that's the feedback loop) from genuine timeline scrubs/jumps. Origin can't do
  // this: timeline scrubs and playback sync both write origin "internal".
  const isPlaybackWriteRef = useRef<boolean>(false);

  // Last absolute timestamp the RAF loop wrote. Used to skip no-op writes (a
  // stalled video keeps isPlaying=true but freezes currentTime, so the computed
  // timestamp repeats) which would otherwise arm isPlaybackWriteRef without ever
  // running the seek detector to consume it — leaving it stuck true.
  const lastPlaybackWriteMsRef = useRef<number | null>(null);

  // Track previous video URL to detect changes (for flicker-free transitions)
  /**
   * Which CLIP is currently loaded, by id. Not the signed URL — the API re-signs
   * per request, so the URL changes constantly for an unchanged clip.
   */
  const prevVideoIdRef = useRef<number | null>(null);

  // Standby video element for preloading the next segment
  const standbyRef = useRef<HTMLVideoElement | null>(null);
  /** The clip the standby element has warmed, with the exact URL it warmed. */
  const standbyClipRef = useRef<{ id: number; url: string } | null>(null);


  // Custom hooks for video data management
  const {
    currentVideo,
    sortedVideos,
    currentVideoIndex,
    isLoading,
    isDataChanging,
    handlePrevVideo: prevVideo,
    handleNextVideo: nextVideo,
    handleGoToVideo: goToVideo,
    footageStartMs,
    footageEndMs,
    liveClampActive,
  } = useVideoData({
    device_mxid,
    live: liveActive,
    // Only the live primary honors Go-live jumps; followers reset via their own
    // (live=false) path when the shared cursor moves.
    //
    // The jump targets the BUFFERED live position, not the wall clock. Landing
    // on the edge is what starves playback in the first place — and since every
    // re-anchor is forward-only (a backward cursor write would read as a scrub
    // and drop us out of live), nothing would pull it back afterwards. So
    // "Go live" has to land where playback can actually survive.
    jumpToLiveTime: liveActive ? bufferedJumpToLiveTime : null,
  });

  // Handler for when video ends - auto-play next if continuous playback is enabled
  const handleVideoEnded = () => {
    // Live-following rolls continuously even if the user never toggled autoplay.
    //
    // A FOLLOWER on the live map rolls too. It is `liveActive: false` (only the
    // primary drives live playback) and `continuousPlayback` is off by default,
    // so this handler used to do nothing at all for it: the tile hit the end of
    // its clip and sat there until the shared cursor happened to drag it onto
    // the next one, which is a visible freeze at every rollover. Measured on
    // Casa de Pedra: the follower stalled in 29% of samples against the
    // primary's 5%. Stepping forward is safe — a follower's own `+1` lands on
    // the clip the cursor is about to reach anyway, and the cursor seek keeps it
    // in sync from there.
    const rollForward = continuousPlayback || liveActive || (followerMode && liveFollowing);
    const lastIndex = sortedVideos.length - 1;
    // Live-follow re-anchors FORWARD on rollover rather than stepping +1, which
    // would let playback drift permanently behind (every rollover costs
    // wall-clock time 1x playback never recovers).
    //
    // But it re-anchors to a BUFFER behind the newest clip, not to the newest
    // clip itself. Footage becomes fetchable 17–68 s after a clip ends, so a
    // player pinned to the edge runs out of material before the next file lands:
    // replaying real arrivals, pinning to the newest clip plays 34% of the time
    // with 35 stalls in 28 minutes, and that stalling — not the rendering — is
    // what the operator sees as the timeline jumping.
    //
    // `liveClipIndex` can only return something at or behind the newest, and the
    // `> currentVideoIndex` guard keeps this strictly forward: a backward write
    // would read as a scrub to `LiveEdgeDriver` and drop us out of live.
    const bufferMs = liveBufferMs(useVideoLibraryStore.getState().getClipArrivals(device_mxid), liveDelayMs);
    const buffered = liveClipIndex(sortedVideos, bufferMs);
    const liveTarget = nextLiveClipIndex(currentVideoIndex, buffered);
    const nextIndex = liveActive ? liveTarget : currentVideoIndex + 1;
    if (rollForward && currentVideoIndex < lastIndex) {
      goToVideo(nextIndex);

      // Only a tile that DRIVES playback may move the shared cursor. A follower
      // advancing its own clip must never write it — two tiles writing the one
      // cursor fight each other, and a follower's write would drag the primary
      // (and every other row) onto the follower's position.
      if (!followerMode && sortedVideos[nextIndex]) {
        const videoStartTime = new Date(sortedVideos[nextIndex].start_time).getTime();
        setSelectedTime(videoStartTime, "video");
      }

      setTimeout(() => {
        videoRef.current?.play();
      }, 100);
    } else if (liveActive) {
      // Caught up to the newest clip with nothing after it: arm resume-on-arrival
      // (the live-resume effect below advances into the next clip once it lands).
      waitingForFootageRef.current = true;
    }
  };

  // Custom hooks for video playback controls
  const {
    videoRef,
    isPlaying,
    setIsPlaying,
    currentTime,
    duration,
    handlePlayPause,
    handleSkipBack,
    handleSkipForward,
    handleDownload,
    handleShare,
    handleFullscreen,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleEnded,
    handleSeek,
    handleSeekComplete,
    formatTime,
    playbackRate,
    handlePlaybackRateChange,
  } = useVideoPlayback({ currentVideo, onVideoEnded: handleVideoEnded });

  // Live map primary: the newest clip can STALL at its end — firing `waiting` /
  // `stalled`, never `ended` — when it's the last uploaded footage and the next
  // clip hasn't landed yet (a still-finalizing file whose playhead runs out of
  // buffered media). Because `ended` never fires, `handleVideoEnded` never arms
  // resume-on-arrival, so playback would stay frozen at the edge even after the
  // next clip arrives (the resume effect below bails on the unset flag). Arm it
  // here on the stall instead, under the SAME "caught up to the live edge"
  // conditions `handleVideoEnded` implies: live, on the newest loaded clip, at/near
  // the clip end, and actively trying to play. A deliberate pause emits `pause`
  // (not `waiting`) — the `el.paused` guard keeps this from ever firing against a
  // user stop, so leaving live on a real mid-footage pause is untouched. A mid-clip
  // network hiccup (playhead far from the end) is rejected by the near-end guard.
  const handleVideoStalledAtEdge = () => {
    const el = videoRef.current;
    if (!liveActive || !el || el.paused) return;
    if (currentVideoIndex !== sortedVideos.length - 1) return;
    if (!isFinite(el.duration) || el.duration - el.currentTime > LIVE_STALL_END_TOL_SECONDS) return;
    waitingForFootageRef.current = true;
  };

  // --- Context sync effects (primary only) ---
  useEffect(() => {
    if (!followerMode) setIsVideoPlaying(isPlaying);
  }, [isPlaying, setIsVideoPlaying, followerMode]);

  // Belt-and-suspenders: a stopped video produces no RAF writes, so any armed
  // playback-write flag can't be legitimately consumed — clear it on pause so a
  // stale arm can never swallow the next user seek.
  useEffect(() => {
    if (!isPlaying) {
      isPlaybackWriteRef.current = false;
      lastPlaybackWriteMsRef.current = null;
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!followerMode) setIsVideoLinked(syncWithTimeline);
  }, [syncWithTimeline, setIsVideoLinked, followerMode]);

  useEffect(() => {
    if (!followerMode) setVideoPlaybackRate(playbackRate);
  }, [playbackRate, setVideoPlaybackRate, followerMode]);

  // A follower's speed button has no element worth driving (the mirror effect
  // below overwrites it from `primaryPlaybackRate`), so it writes the SHARED
  // rate instead — which only means anything if the primary, the tile that
  // actually owns playback, adopts it. Together with the publish effect above
  // this is a two-way bind that settles in ONE round: adopting sets the local
  // rate, whose publish re-writes the identical value and is a no-op. Inert on
  // machine-detail, where the single player is the only writer and the two
  // values are therefore always already equal. Handler read through a ref so
  // this doesn't re-run on every render (it isn't memoized upstream).
  const handlePlaybackRateChangeRef = useRef(handlePlaybackRateChange);
  handlePlaybackRateChangeRef.current = handlePlaybackRateChange;
  useEffect(() => {
    if (followerMode || primaryPlaybackRate === playbackRate) return;
    handlePlaybackRateChangeRef.current(primaryPlaybackRate);
  }, [followerMode, primaryPlaybackRate, playbackRate]);

  // --- Follower sync effects ---
  // Suspended while this tile is in native fullscreen: there the user drives the
  // element with the browser's own controls, and mirroring the primary's play
  // state would undo their pause (see the fullscreen effect below).
  useEffect(() => {
    if (!followerMode || isFullscreenRef.current || !videoRef.current || !currentVideo) return;
    if (primaryIsPlaying && videoRef.current.paused) {
      videoRef.current.play().catch(() => {});
    } else if (!primaryIsPlaying && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  }, [followerMode, primaryIsPlaying, currentVideo, videoRef]);

  useEffect(() => {
    if (!followerMode || !videoRef.current) return;
    if (videoRef.current.playbackRate !== primaryPlaybackRate) {
      videoRef.current.playbackRate = primaryPlaybackRate;
    }
  }, [followerMode, primaryPlaybackRate, videoRef]);

  // Latest clip in a ref so the cursor subscription (set up once) always seeks
  // against the current clip without re-subscribing on every clip switch.
  const currentVideoRef = useRef(currentVideo);
  currentVideoRef.current = currentVideo;

  // Followers get no `timeupdate` (see the <video>), so nothing samples their
  // position into `currentTime` — which a follower's scrubber needs while it is
  // PAUSED (playing, its own rAF reads the element directly). Sample exactly
  // where the shared cursor repositions the element, throttled so a follower
  // that can't keep sync — a rejected autoplay, a stalled buffer — can't turn
  // the ~30/sec cursor ticks into ~30 re-renders/sec. Handler via ref: it isn't
  // memoized upstream, and `seekFollowerToCursor` must keep a stable identity or
  // the cursor subscription below re-subscribes on every render.
  const handleTimeUpdateRef = useRef(handleTimeUpdate);
  handleTimeUpdateRef.current = handleTimeUpdate;
  const lastFollowerSampleRef = useRef(0);

  // The primary's play state, read from mount-stable cursor subscriptions below,
  // so it has to come from a ref rather than a closed-over render value.
  const primaryIsPlayingRef = useRef(primaryIsPlaying);
  primaryIsPlayingRef.current = primaryIsPlaying;

  // Previous cursor value this follower saw. The DELTA is what distinguishes the
  // primary's playback cadence from a scrub — origin cannot, since a timeline
  // scrub and playback sync both write "internal".
  const lastCursorMsRef = useRef<number | null>(null);
  const lastStuckSeekRef = useRef(0);
  const lastResumeTryRef = useRef(0);

  // Seek a follower's element onto the shared cursor. Reads the cursor from the
  // store (not a render value) so followers don't re-render on playback ticks.
  // Suspended in native fullscreen for the same reason as the play/pause mirror:
  // the shared cursor must not drag the playhead out from under a user scrubbing
  // with the native controls.
  const seekFollowerToCursor = useCallback(() => {
    if (!followerMode || isFullscreenRef.current) return;
    const el = videoRef.current;
    const cv = currentVideoRef.current;
    const st = cursorStore.getSnapshot().selectedTime;
    if (!el || !cv || !st) return;

    // Tracked before every bail-out below, so the delta always measures one
    // cursor tick rather than the gap since the last time a seek was allowed.
    const prevCursorMs = lastCursorMsRef.current;
    lastCursorMsRef.current = st;

    const videoStartMs = new Date(cv.start_time).getTime();
    const videoEndMs = new Date(cv.end_time).getTime();
    if (st < videoStartMs || st > videoEndMs) return;

    const expectedPos = computeSeekPosition(cv.start_time, st);
    if (Math.abs(expectedPos - el.currentTime) <= 0.5) return;

    // A follower that should be playing but is paused needs RESTARTING, not
    // dragging (see FOLLOWER_STUCK_RESEEK_MS). A genuine scrub still moves it at
    // once — the cursor jumps then, which playback never does.
    const now = performance.now();
    const scrubbed = prevCursorMs == null || Math.abs(st - prevCursorMs) > FOLLOWER_SCRUB_JUMP_MS;
    if (el.paused && primaryIsPlayingRef.current && !scrubbed) {
      if (now - lastStuckSeekRef.current < FOLLOWER_STUCK_RESEEK_MS) return;
      lastStuckSeekRef.current = now;
    }

    el.currentTime = expectedPos;
    if (el.paused && now - lastFollowerSampleRef.current > FOLLOWER_SAMPLE_THROTTLE_MS) {
      lastFollowerSampleRef.current = now;
      handleTimeUpdateRef.current();
    }
  }, [followerMode, cursorStore, videoRef]);

  // Cursor-driven follower seek: subscribe (no re-render per tick).
  useEffect(() => {
    if (!followerMode) return;
    return cursorStore.subscribe(seekFollowerToCursor);
  }, [followerMode, cursorStore, seekFollowerToCursor]);

  // Followers only: retry play() on a tile that should be playing but is not.
  //
  // The mirror effect below is edge-triggered — it runs when the primary's play
  // state changes or when this tile's clip changes — and it swallows a rejected
  // play(). A source switch routinely rejects one with AbortError ("interrupted
  // by a new load request"), and after that nothing ever asks again: the primary
  // keeps playing so `primaryIsPlaying` never changes, and this tile's clip
  // cannot change either, because rolling to the next clip needs an `ended`
  // event that only a PLAYING element fires. Measured on the map: a follower
  // paused for 100% of a four-minute window, with no `playing`, `waiting` or
  // `stalled` event in it — the element was never running at all.
  //
  // Driven off the cursor subscription rather than its own timer: it already
  // ticks while the primary plays, which is exactly when a stopped follower is
  // wrong, and it falls silent when playback stops.
  const resumeFollowerIfStopped = useCallback(() => {
    if (!followerMode || isFullscreenRef.current) return;
    const el = videoRef.current;
    if (!el || !el.paused || !primaryIsPlayingRef.current) return;
    // An ENDED element belongs to the rollover path — play() here would restart
    // the clip from zero instead of advancing into the next one.
    if (el.ended || el.readyState < HAVE_CURRENT_DATA) return;
    const now = performance.now();
    if (now - lastResumeTryRef.current < FOLLOWER_RESUME_RETRY_MS) return;
    lastResumeTryRef.current = now;
    el.play().catch(() => {});
  }, [followerMode, videoRef]);

  useEffect(() => {
    if (!followerMode) return;
    return cursorStore.subscribe(resumeFollowerIfStopped);
  }, [followerMode, cursorStore, resumeFollowerIfStopped]);

  // Re-seek to the current cursor when the clip changes (old effect's currentVideo dep).
  useEffect(() => {
    seekFollowerToCursor();
  }, [currentVideo, seekFollowerToCursor]);

  // --- Seek detection ---
  // Driven by a store subscription (not a render dep) so this tile never
  // re-renders on cursor ticks. The store notifies SYNCHRONOUSLY on every real
  // change and dedupes no-ops exactly like React setState did, so the RAF's
  // arm→consume of `isPlaybackWriteRef` stays 1:1 (see updateTimelineCursor).
  useEffect(() => {
    const detectSeek = () => {
      const snap = cursorStore.getSnapshot();
      const st = snap.selectedTime;
      if (!st) return;

      // The video wrote this cursor update itself while playing — never reseek in
      // response to our own playback, regardless of jump size (rate spikes / tab
      // resume can exceed the magnitude threshold below). This is the root guard
      // that breaks the playback -> cursor -> reseek feedback loop.
      if (isPlaybackWriteRef.current) {
        isPlaybackWriteRef.current = false;
        lastSelectedTimeRef.current = st;
        return;
      }

      const timeDiff = lastSelectedTimeRef.current
        ? Math.abs(st - lastSelectedTimeRef.current)
        : Infinity;

      if (timeDiff > 1000 || lastSelectedTimeRef.current === null) {
        pendingSeekTimeRef.current = st;
        seekCooldownRef.current = performance.now();
        isUserSeekRef.current = true;
        // `pendingSeekTimeRef` is consumed by the source-switch effect below,
        // which only re-runs when `currentVideo` changes — so a seek landing
        // INSIDE the clip already loaded would never reach the element. Apply it
        // here for:
        //
        // - "video": cursor writes another VIDEO TILE made (a follower's scrub or
        //   skip), which has to move this primary too. This primary's OWN "video"
        //   writes are clip transitions, whose target lies outside the clip still
        //   loaded here, so they stay no-ops.
        // - "external": a PROGRAMMATIC seek — the map's alert seek (feed click,
        //   marker, and the alert card's Prev/Next), go-live, table clicks. Alert
        //   Prev/Next steps to a neighbouring alert, which very often lands inside
        //   the clip already loaded: `currentVideo` never changes, the source-switch
        //   effect never re-runs, and the video sat where it was while the timeline
        //   and card moved on. That is the "video doesn't load on Prev/Next" report.
        //
        // Timeline scrubs write "internal" and are deliberately still excluded, so
        // scrubbing keeps its exact existing behaviour everywhere (machine-detail
        // included). Read through refs: this subscription must stay mount-stable
        // (its dep list is `[cursorStore]`), because re-subscribing would
        // reorder the store's listeners and break the RAF's arm -> consume
        // pairing on `isPlaybackWriteRef`.
        if (
          (snap.lastOrigin === "video" || snap.lastOrigin === "external") &&
          !followerModeRef.current
        ) {
          // Applied in full → drop the pending seek, so a later clip change can't
          // re-apply this now-stale target and yank playback backwards. When the
          // target lies outside the loaded clip this no-ops and the pending seek
          // survives for the source-switch effect to consume once the right clip
          // is fetched and mounted.
          if (seekVideoToTimeRef.current(st)) pendingSeekTimeRef.current = null;
        }
      }

      lastSelectedTimeRef.current = st;
    };

    const unsub = cursorStore.subscribe(detectSeek);
    // Match the old effect's mount-run: catches a cursor seeded before this
    // subscription was set up (e.g. CursorInit fires before the video mounts).
    detectSeek();
    return unsub;
  }, [cursorStore]);

  /** Returns whether the target was handled by the clip currently loaded. */
  const seekVideoToTime = useCallback((timestamp: number): boolean => {
    if (!currentVideo || !videoRef.current) return false;

    const videoStartTime = new Date(currentVideo.start_time).getTime();
    const videoEndTime = new Date(currentVideo.end_time).getTime();
    if (timestamp < videoStartTime || timestamp > videoEndTime) return false;

    // Live (or was-live): don't seek onto the clip's end — that stalls it at
    // 'ended' with the cursor stuck ahead of footage. Leave it playing; the RAF
    // loop walks the cursor forward onto real footage. Uses `liveClampActive` so
    // the guard survives the leave-live (pause) transition while footage exists.
    if (liveClampActive && timestamp >= videoEndTime - LIVE_SEEK_END_GUARD_MS) return false;

    const seekPosition = computeSeekPosition(currentVideo.start_time, timestamp);

    if (videoRef.current.readyState >= 1) {
      if (Math.abs(videoRef.current.currentTime - seekPosition) > SEEK_TOLERANCE_SECONDS) {
        videoRef.current.currentTime = seekPosition;
      }
    } else {
      const onCanPlay = () => {
        if (videoRef.current && Math.abs(videoRef.current.currentTime - seekPosition) > SEEK_TOLERANCE_SECONDS) {
          videoRef.current.currentTime = seekPosition;
        }
        videoRef.current?.removeEventListener("canplay", onCanPlay);
      };
      videoRef.current.addEventListener("canplay", onCanPlay);
    }
    return true;
  }, [currentVideo, videoRef, liveClampActive]);

  // Reachable from the mount-stable seek detector above without making it a
  // subscription dependency (which would re-subscribe on every clip change).
  const seekVideoToTimeRef = useRef(seekVideoToTime);
  seekVideoToTimeRef.current = seekVideoToTime;

  // Prime the element's position to the shared cursor as soon as metadata loads,
  // so the FIRST Play has a buffered frame to start from — without this, playback
  // needed a scrub to "prime" the currentTime before it would start. Tolerance in
  // seekVideoToTime keeps this a no-op when the position is already correct.
  const handleLoadedMetadataAndPrime = useCallback(() => {
    handleLoadedMetadata();
    const st = cursorStore.getSnapshot().selectedTime;
    if (st != null) {
      seekVideoToTime(st);
    }
  }, [handleLoadedMetadata, seekVideoToTime, cursorStore]);

  // --- Preload next video on hidden standby element ---
  // Live-follow re-anchors to the NEWEST clip on rollover (see handleVideoEnded),
  // so warm that one instead of the immediate next — otherwise a multi-clip
  // catch-up lands on a cold fetch, and the resulting load stall can outlast
  // LiveEdgeDriver's pause debounce and silently drop us out of live.
  useEffect(() => {
    const nextVid = sortedVideos[liveActive ? sortedVideos.length - 1 : currentVideoIndex + 1];
    if (!nextVid || (liveActive && currentVideoIndex >= sortedVideos.length - 1)) {
      standbyClipRef.current = null;
      return;
    }

    // Keyed by clip ID, not by URL — for the same reason the source-switch effect
    // below is. `/api/videos/` re-signs on every request, so `signedUrl` differs
    // on each ~5s poll and this guard never matched: the standby element set a
    // new `src` and called `load()` every poll, re-downloading the SAME upcoming
    // clip over and over. A preloader meant to smooth rollover was instead
    // competing with the playing video for bandwidth, several times a minute.
    if (standbyClipRef.current?.id === nextVid.id) return;

    if (!standbyRef.current) {
      standbyRef.current = document.createElement("video");
      standbyRef.current.preload = "auto";
      standbyRef.current.muted = true;
    }

    standbyRef.current.src = nextVid.signedUrl;
    standbyRef.current.load();
    // Remember the exact URL that was warmed. The switch has to reuse it or the
    // preload buys nothing: a fresher signature for the same clip is a different
    // cache key, so the element would refetch from cold and stall exactly where
    // this exists to prevent a stall.
    standbyClipRef.current = { id: nextVid.id, url: nextVid.signedUrl };
  }, [currentVideoIndex, sortedVideos, liveActive]);

  // Cleanup standby element on unmount
  useEffect(() => {
    return () => {
      if (standbyRef.current) {
        standbyRef.current.src = "";
        standbyRef.current = null;
      }
    };
  }, []);

  // --- Video source switching ---
  useEffect(() => {
    if (!currentVideo || !videoRef.current) return;

    // ALWAYS the freshest signature, never the one the standby warmed.
    //
    // Reusing the warmed URL to guarantee a cache hit looks free and is not:
    // these signatures are short-lived, the standby warms the NEXT clip which
    // may then sit unused for a minute or more, and an expired URL arrives here
    // as a media error — a stall exactly where the preload was supposed to
    // prevent one. Handing the element a fresh URL keeps `needsSource`'s expiry
    // recovery meaningful. The preload still helps whenever the signature has
    // not rolled; it just is not relied upon.
    const newUrl = currentVideo.signedUrl;
    const video0 = videoRef.current;
    // Identify the clip by its ID, NOT by its signed URL.
    //
    // `/api/videos/` re-signs on every request: the same clip comes back with a
    // fresh signature each ~5s poll (measured: 55 of 55 clips changed URL, one
    // of them 8 times across 11 responses). Comparing URLs therefore read every
    // poll as "a different video" and ran `src = url; load()` below — which
    // restarts playback. That is the "video stops on update" report, and it is
    // also what yanked the video away while an operator was reviewing an alert.
    //
    // The one case that still NEEDS the fresh URL is an element that failed or
    // never got a source — an expired signature lands here as a media error, so
    // reloading on that keeps expiry handled without reloading on every poll.
    const isNewClip = prevVideoIdRef.current !== currentVideo.id;
    const needsSource = !video0.currentSrc || video0.error != null;
    const isNewVideo = isNewClip || needsSource;

    if (!isNewVideo) {
      if (pendingSeekTimeRef.current !== null) {
        seekVideoToTime(pendingSeekTimeRef.current);
        pendingSeekTimeRef.current = null;
      }
      return;
    }

    prevVideoIdRef.current = currentVideo.id;
    const video = videoRef.current;
    const wasPlaying = !video.paused;

    video.src = newUrl;
    video.load();
    video.playbackRate = playbackRate;

    const seekTime = pendingSeekTimeRef.current;
    const wasUserSeek = isUserSeekRef.current;
    isUserSeekRef.current = false;
    const onCanPlay = () => {
      if (videoRef.current && seekTime !== null) {
        const videoStartTime = new Date(currentVideo.start_time).getTime();
        const videoEndTime = new Date(currentVideo.end_time).getTime();
        // Live (or was-live): never restore a seek onto the clip's end (it stalls
        // at 'ended'); let the fresh clip play from its start and the RAF loop
        // carry the cursor. `liveClampActive` keeps this active through leave-live.
        const liveEndClamp = liveClampActive && seekTime >= videoEndTime - LIVE_SEEK_END_GUARD_MS;
        if (seekTime >= videoStartTime && seekTime <= videoEndTime && !liveEndClamp) {
          videoRef.current.currentTime = computeSeekPosition(currentVideo.start_time, seekTime);
        }
        pendingSeekTimeRef.current = null;
      }
      if (wasPlaying && !wasUserSeek) {
        videoRef.current?.play().catch(() => {});
      }
      video.removeEventListener("canplay", onCanPlay);
    };
    video.addEventListener("canplay", onCanPlay);
    // `isLoading` / `isDataChanging` are deps because the <video> is mounted
    // CONDITIONALLY behind them, and this effect writes to `videoRef.current`.
    //
    // Without them: clips arrive and set `currentVideo` while the spinner is
    // still up, so this runs with `videoRef.current === null` and returns at the
    // guard above. When the spinner clears, a fresh <video> mounts with no source
    // — but `currentVideo` has not changed, so nothing re-runs this and the
    // element keeps `src=""` / readyState 0 indefinitely. Measured after Go-live:
    // a mounted <video> stuck at 0:00 for 100 s while its clips were loaded,
    // which is the "video stuck at 0:00 while segments exist" report.
    //
    // Re-running is cheap: `isNewVideo` below reassigns the source only for a new
    // clip id or an element that has none, so a spinner toggle alone never
    // restarts playback.
  }, [currentVideo, seekVideoToTime, playbackRate, liveClampActive, isLoading, isDataChanging]);

  // --- Navigation ---
  const handlePrevVideo = () => {
    if (currentVideoIndex <= 0) return;
    prevVideo();
    setIsPlaying(false);
    const prevIndex = currentVideoIndex - 1;
    if (sortedVideos[prevIndex]) {
      setSelectedTime(new Date(sortedVideos[prevIndex].start_time).getTime(), "video");
    }
  };

  const handleNextVideo = () => {
    if (currentVideoIndex >= sortedVideos.length - 1) return;
    nextVideo();
    setIsPlaying(false);
    const nextIndex = currentVideoIndex + 1;
    if (sortedVideos[nextIndex]) {
      setSelectedTime(new Date(sortedVideos[nextIndex].start_time).getTime(), "video");
    }
  };

  useVideoKeyboardShortcuts({
    handlePrevVideo,
    handleNextVideo,
    handlePlayPause,
    handleSkipBack,
    handleSkipForward,
    currentVideoIndex,
    totalVideos: sortedVideos.length,
    isPlaying,
    disabled: followerMode,
  });

  // --- RAF timeline cursor sync (primary only) ---
  const updateTimelineCursor = useCallback(() => {
    if (followerMode || !syncWithTimeline || !isPlaying || !currentVideo || !videoRef.current) {
      return;
    }

    const now = performance.now();

    if (now - seekCooldownRef.current < 500) {
      rafIdRef.current = requestAnimationFrame(updateTimelineCursor);
      return;
    }

    if (now - lastTimelineUpdateRef.current < 33) {
      rafIdRef.current = requestAnimationFrame(updateTimelineCursor);
      return;
    }
    lastTimelineUpdateRef.current = now;

    const absoluteTimestamp = computeAbsoluteTimestamp(currentVideo.start_time, videoRef.current.currentTime);
    // Only sync (and arm the flag) when the position actually advanced. Writing
    // the same value is a React no-op that never runs the seek-detector effect,
    // so it would leave isPlaybackWriteRef armed but never consumed — and the
    // next genuine seek would be swallowed. Skipping keeps arm -> consume 1:1.
    if (absoluteTimestamp !== lastPlaybackWriteMsRef.current) {
      lastPlaybackWriteMsRef.current = absoluteTimestamp;
      isPlaybackWriteRef.current = true;
      setSelectedTime(absoluteTimestamp, "internal");
    }

    rafIdRef.current = requestAnimationFrame(updateTimelineCursor);
  }, [followerMode, syncWithTimeline, isPlaying, currentVideo, videoRef, setSelectedTime]);

  useEffect(() => {
    if (!(!followerMode && syncWithTimeline && isPlaying && currentVideo)) return;

    const arm = () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(updateTimelineCursor);
    };
    arm();

    // This loop is a self-perpetuating rAF chain: every link is requested from
    // inside the previous callback, so ONE callback that never arrives ends it
    // permanently. A backgrounded tab is entitled to do exactly that — a frozen
    // or discarded page drops its pending frames — and none of this effect's
    // deps change on the way back, so nothing re-arms it until the clip does.
    //
    // That is the reported failure exactly: return to the tab and the active
    // video keeps playing (the element needs no rAF) while the shared cursor,
    // the follower tile, the transport and the lag chip are all frozen — every
    // one of them downstream of the cursor, which has this loop as its only
    // writer — and it heals when the clip ends, because `currentVideo` changing
    // is what re-runs this effect.
    //
    // Two ways back, because the chain can break for reasons other than the tab:
    // re-arm on becoming visible, and poll for a cursor that has gone quiet
    // while it should be moving. `arm()` cancels before requesting, so neither
    // can leave two frames in flight.
    const onVisible = () => {
      if (!document.hidden) arm();
    };
    document.addEventListener("visibilitychange", onVisible);
    const watchdog = setInterval(() => {
      if (document.hidden || !videoRef.current || videoRef.current.paused) return;
      if (performance.now() - lastTimelineUpdateRef.current > CURSOR_STALL_MS) arm();
    }, CURSOR_STALL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(watchdog);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [followerMode, syncWithTimeline, isPlaying, currentVideo, videoRef, updateTimelineCursor]);

  // "Go live" (or entering the map's realtime mode) bumps playRequest to command
  // the PRIMARY to start playing — the click is the user gesture browsers need
  // for autoplay. Followers keep mirroring the primary via primaryIsPlaying, so
  // they must never act on this. Ignored (playRequest starts at 0) everywhere
  // else, so machine-detail is unaffected.
  useEffect(() => {
    if (followerMode || !playRequest || !currentVideo || !videoRef.current) return;
    videoRef.current.play().catch(() => {});
  }, [playRequest, followerMode, currentVideo, videoRef]);

  // The mirror of the above: a follower's pause button bumps `pauseRequest` to
  // stop the PRIMARY, whose `onPause` then clears the shared `isVideoPlaying`
  // and the follower-mirror effect pauses every other tile. Same shape as a
  // pause pressed on the primary itself, so the map's leave-live watcher reads
  // it identically. Ignored (starts at 0) where nothing calls it.
  useEffect(() => {
    if (followerMode || !pauseRequest || !videoRef.current) return;
    // Routed from another tile's transport, but still the operator's stop.
    userPausedRef.current = true;
    videoRef.current.pause();
  }, [pauseRequest, followerMode, videoRef]);

  // --- Map live-follow: keep the primary playing (map-only, `liveAutoplay`) ---
  // Mute the primary while live-following so the browser permits autoplay without
  // a user gesture. Set imperatively (not via a JSX `muted` attribute, which
  // React doesn't reliably reflect to the property). No-op in the editor /
  // machine-detail, where `liveAutoplay` is never set → element stays unmuted.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // Followers are ALWAYS muted; the primary only while live-following.
    //
    // Skipping followers entirely left them unmuted, and the browser refuses
    // unmuted autoplay without a user gesture — so a follower tile never played
    // at all. Measured on Casa de Pedra's second machine: readyState 4 (footage
    // loaded and ready), paused from the start, 0% of real time, 100% of samples
    // stalled, while the primary beside it played. Every "the follower is
    // starving for data" theory was downstream of a tile the browser would not
    // start.
    //
    // Muting them is also the right end state on its own: a follower mirrors the
    // primary, so a location with N machines would otherwise play N soundtracks
    // at once the moment any gesture unlocked them.
    el.muted = followerMode ? true : liveAutoplay;
    // `isLoading` / `isDataChanging` are deps for the same reason the
    // source-switch effect has them: the <video> is mounted CONDITIONALLY behind
    // them, and this writes to `videoRef.current`. Without them the effect ran
    // while the spinner was up, found no element, and never re-ran once one
    // mounted — so the follower kept the element default (unmuted), the browser
    // refused to autoplay it, and it sat at readyState 4 having never played a
    // frame. Muting followers is worthless if the mute never reaches them.
  }, [liveAutoplay, followerMode, currentVideo, videoRef, isLoading, isDataChanging]);

  // Robustly hold the primary at "playing" while live-following: retry play() as
  // soon as the media is ready (loadedmetadata/canplay) and whenever the flag
  // re-arms or the footage switches (currentVideo dep) — not just on the single
  // initial requestPlay. Muted (above) lets these attempts succeed. This does NOT
  // fight leave-live: a deliberate scrub-back or mid-footage pause disarms
  // live-follow up in the page, which flips `liveAutoplay` false and tears these
  // listeners down (a paused/scrubbed element emits no canplay to re-trigger).
  useEffect(() => {
    if (followerMode || !liveAutoplay || !currentVideo) return;
    const el = videoRef.current;
    if (!el) return;
    const tryPlay = () => {
      el.play().catch(() => {});
    };
    tryPlay();
    el.addEventListener("loadedmetadata", tryPlay);
    el.addEventListener("canplay", tryPlay);
    return () => {
      el.removeEventListener("loadedmetadata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
    };
  }, [liveAutoplay, followerMode, currentVideo, videoRef]);

  // Keep it rolling. While live-follow is armed, a stop this element did not ask
  // for is resumed — a stall, a rollover source switch, or footage that arrived
  // late. Deliberately short: the map's leave-live watcher debounces a sustained
  // pause before deciding the operator stopped playback, so resuming well inside
  // that window means an automatic stop never registers as a leave at all.
  //
  // In fullscreen the browser's own controls ARE the transport, so a pause there
  // is by definition the operator's and is recorded as such.
  useEffect(() => {
    if (followerMode || !liveAutoplay || !currentVideo) return;
    const el = videoRef.current;
    if (!el) return;

    let resumeTimer: ReturnType<typeof setTimeout> | null = null;
    const onPause = () => {
      if (isFullscreenRef.current) {
        userPausedRef.current = true;
        return;
      }
      if (userPausedRef.current) return;
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        resumeTimer = null;
        // Re-check on the way out: the operator may have stopped it in between,
        // and live-follow may have been disarmed by something else entirely.
        if (userPausedRef.current || !videoRef.current) return;
        videoRef.current.play().catch(() => {});
      }, LIVE_RESUME_DELAY_MS);
    };
    // Any play — theirs or ours — means the element is no longer user-stopped.
    const onPlay = () => {
      userPausedRef.current = false;
    };

    el.addEventListener("pause", onPause);
    el.addEventListener("play", onPlay);
    return () => {
      if (resumeTimer) clearTimeout(resumeTimer);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("play", onPlay);
    };
  }, [liveAutoplay, followerMode, currentVideo, videoRef]);

  // The backstop for the above: poll for a live primary that is stopped when it
  // should be running (see LIVE_RESUME_WATCHDOG_MS for the deadlock this covers
  // and why no event can).
  //
  // Deliberately NOT keyed on `currentVideo` — a rollover changing the clip is
  // the very moment this has to survive, and depending on it would tear the
  // watchdog down exactly when it is needed. The element is re-read from the ref
  // each tick, so a remount heals on its own.
  useEffect(() => {
    if (followerMode || !liveAutoplay) return;
    const id = setInterval(() => {
      if (isFullscreenRef.current || userPausedRef.current) return;
      const el = videoRef.current;
      if (!el || !el.paused) return;
      // An ENDED element is the rollover path's business — play() would restart
      // the clip it just finished instead of advancing to the next one.
      if (el.ended || el.readyState < HAVE_CURRENT_DATA) return;
      el.play().catch(() => {});
    }, LIVE_RESUME_WATCHDOG_MS);
    return () => clearInterval(id);
  }, [liveAutoplay, followerMode, videoRef]);

  // Re-arming live-follow ("Go live") clears any earlier operator stop — that
  // click IS the instruction to start again.
  useEffect(() => {
    if (liveAutoplay) userPausedRef.current = false;
  }, [liveAutoplay]);

  // --- Leaving live-follow pauses the primary (map only) ---
  // When live-follow disarms — the operator opens an alert (or scrubs back) —
  // the video must stop playing. If it keeps rolling, its RAF loop keeps writing
  // the shared cursor forward and fights the seek to the alert moment: the cursor
  // bounces between the alert time and the live position, so the timeline never
  // settles on the alert and the video never lands on it. Pausing lets the alert
  // seek stick — the cursor holds at the alert timestamp and the video seeks
  // there. "Go live" re-arms `liveAutoplay` and resumes. Primary map tile only
  // (`liveAutoplay` is never set in the editor / machine-detail).
  // Skipped when THIS element is the one that just went fullscreen: there the
  // leave-live is the fullscreen detach itself, and pausing would be the very
  // kind of override the detach exists to stop. Nothing is left pending — the
  // prev-ref still moves to false, so exiting fullscreen fires no late pause.
  const prevLiveAutoplayRef = useRef(liveAutoplay);
  useEffect(() => {
    if (!followerMode && prevLiveAutoplayRef.current && !liveAutoplay && !isFullscreenRef.current) {
      videoRef.current?.pause();
    }
    prevLiveAutoplayRef.current = liveAutoplay;
  }, [liveAutoplay, followerMode, videoRef]);

  // Show the browser's NATIVE video controls only while this element is
  // fullscreen — the custom controls live outside the <video> and aren't visible
  // in fullscreen, so without this fullscreen has no scrubber/volume. Toggled
  // imperatively on fullscreenchange so inline playback keeps the custom controls.
  // Lives in the shared component, so it runs per TILE — primary and follower
  // alike — and each one switches only its OWN element's native controls.
  //
  // Entering fullscreen also DETACHES this tile from live control: the native
  // controls are useless while live-follow keeps re-seeking / re-playing the
  // element under the user (retry-play on canplay, the 5s catch-up jump,
  // resume-on-arrival, the follower's cursor seek). `onLeaveLive` disarms the
  // page's existing live-follow flag — the same path a scrub-back or a pause
  // takes — which silences every live writer in this component at once. Leaving
  // fullscreen deliberately does NOT re-arm: the operator is left where they
  // scrubbed to, and the stack's "Go live" button (already visible once follow is
  // off) is the way back.
  //
  // `currentVideo` is a dep because the <video> is mounted conditionally: on the
  // first commits the ref is still null, so without it the element-level
  // (iOS) listeners would never be attached.
  useEffect(() => {
    const el = videoRef.current;
    // Both directions matter: entering shows the native controls, LEAVING must
    // restore `false` so the custom bar is the only inline transport again.
    const applyFullscreen = (next: boolean) => {
      const node = videoRef.current;
      if (node) node.controls = next;
      const was = isFullscreenRef.current;
      isFullscreenRef.current = next;
      // Rising edge only — a repeat event (another element entering fullscreen
      // re-fires the document listener) must not re-report.
      if (next && !was) onLeaveLive?.();
    };
    const syncNativeControls = () => {
      const node = videoRef.current;
      if (!node) return;
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      applyFullscreen(document.fullscreenElement === node || doc.webkitFullscreenElement === node);
    };
    // iOS Safari opens its own native player instead of putting the element in
    // the fullscreen tree, so neither document event fires and
    // `webkitFullscreenElement` stays null — its begin/end pair is the only
    // signal there.
    const onWebkitBegin = () => applyFullscreen(true);
    const onWebkitEnd = () => applyFullscreen(false);
    document.addEventListener("fullscreenchange", syncNativeControls);
    document.addEventListener("webkitfullscreenchange", syncNativeControls);
    el?.addEventListener("webkitbeginfullscreen", onWebkitBegin);
    el?.addEventListener("webkitendfullscreen", onWebkitEnd);
    return () => {
      document.removeEventListener("fullscreenchange", syncNativeControls);
      document.removeEventListener("webkitfullscreenchange", syncNativeControls);
      el?.removeEventListener("webkitbeginfullscreen", onWebkitBegin);
      el?.removeEventListener("webkitendfullscreen", onWebkitEnd);
    };
  }, [videoRef, currentVideo, onLeaveLive]);

  // --- Map live-follow: report the newest footage edge up to the page ---
  // Only while live (and only for the wired primary). The page pins the shared
  // cursor to `startMs` (playable) and treats `endMs` as the true live edge.
  const lastFootageReportRef = useRef<string>("");
  useEffect(() => {
    if (!onFootageEdgeChange) return;
    const edge =
      liveActive && footageStartMs != null && footageEndMs != null
        ? { startMs: footageStartMs, endMs: footageEndMs }
        : null;
    const key = edge ? `${edge.startMs}:${edge.endMs}` : "null";
    if (key === lastFootageReportRef.current) return;
    lastFootageReportRef.current = key;
    onFootageEdgeChange(edge);
  }, [liveActive, footageStartMs, footageEndMs, onFootageEdgeChange]);

  // --- Map live-follow: resume into a newly-arrived clip ---
  // When live and caught up to the newest clip's end (waiting flag set by
  // handleVideoEnded), advancing into the clip that just landed keeps playback
  // rolling at the live edge. Gated on the waiting flag so it never fights a
  // deliberate mid-footage pause (which disarms live-follow up in the page).
  // Resumes at the NEWEST clip, not the next one — if several landed while we
  // waited (or the tab was backgrounded), the older ones are already stale.
  useEffect(() => {
    if (!liveActive || !waitingForFootageRef.current) return;
    const nextIndex = sortedVideos.length - 1;
    if (currentVideoIndex >= nextIndex) return;
    waitingForFootageRef.current = false;
    goToVideo(nextIndex);
    const next = sortedVideos[nextIndex];
    if (next) setSelectedTime(new Date(next.start_time).getTime(), "video");
    setTimeout(() => {
      videoRef.current?.play().catch(() => {});
    }, 100);
  }, [liveActive, sortedVideos, currentVideoIndex, goToVideo, setSelectedTime, videoRef]);

  // --- Map live-follow: catch up to the newest footage ---
  // Belt-and-suspenders re-anchor. The per-rollover jump (handleVideoEnded) and
  // the resume-on-arrival effect above normally hold the tile at the live edge,
  // but if playback falls behind the newest LOADED clip — a long clip, a slow
  // rollover, or a stall the resume flag missed — the tile can sit MINUTES behind
  // footage that's already available (the live-tracker-5-min-behind bug). Every
  // few seconds, if the newest loaded clip has pulled well ahead of the one we're
  // playing, jump straight to it. Reads the latest data from refs so the interval
  // isn't re-created on every clip change; the newest-loaded check means it never
  // fires when there's simply no fresher footage (real upload lag).
  // Clips run ~30s, so this is roughly one clip of tolerance.
  const CATCH_UP_GAP_MS = 40_000;
  const catchUpSortedRef = useRef(sortedVideos);
  catchUpSortedRef.current = sortedVideos;
  const catchUpIndexRef = useRef(currentVideoIndex);
  catchUpIndexRef.current = currentVideoIndex;
  useEffect(() => {
    if (!liveActive) return;
    const id = setInterval(() => {
      const sorted = catchUpSortedRef.current;
      const idx = catchUpIndexRef.current;
      if (sorted.length - 1 <= idx) return; // already on the newest loaded clip — nothing fresher

      // Catch up to the BUFFERED live position, not to the newest clip: landing
      // on the edge is what starves playback in the first place. Forward only —
      // `shouldRealignToBuffer` refuses a target at or behind the current clip.
      const bufferMs = liveBufferMs(useVideoLibraryStore.getState().getClipArrivals(device_mxid), liveDelayMs);
      const target = liveClipIndex(sorted, bufferMs);
      // One clip of slack, so ordinary one-clip-ahead playback is never cut off
      // mid-clip; anything more than that is playback genuinely stuck behind
      // footage we already hold, which is what this exists to rescue.
      if (target == null || !shouldRealignToBuffer(idx, target, sorted, CATCH_UP_GAP_MS)) return;
      goToVideo(target);
      setSelectedTime(new Date(sorted[target].start_time).getTime(), "video");
      setTimeout(() => videoRef.current?.play().catch(() => {}), 100);
    }, 5000);
    return () => clearInterval(id);
  }, [liveActive, goToVideo, setSelectedTime, videoRef]);

  // --- Transport: one bar, two kinds of tile ---------------------------------
  // The PRIMARY owns playback: its element drives the shared cursor, and the
  // mirror effects above push its play state / rate onto every follower. So a
  // follower's bar cannot drive its own element — the mirrors would overwrite it
  // — and instead mutates the SHARED state the primary already reacts to:
  //   play/pause -> requestPlay / requestPause  (primary acts, mirrors follow)
  //   seek/skip  -> the shared cursor           (primary seeks, followers snap)
  //   speed      -> the shared rate             (primary adopts, mirrors follow)
  // Fullscreen (and any future volume/mute) stay genuinely per-tile: they touch
  // only this element, so one tile can be expanded or muted on its own.
  // The primary keeps its original local handlers verbatim — it IS the shared
  // state's source — which is why machine-detail is untouched by all of this.
  const shownIsPlaying = followerMode ? primaryIsPlaying : isPlaying;
  const shownPlaybackRate = followerMode ? primaryPlaybackRate : playbackRate;

  const handleTransportPlayPause = () => {
    // The operator's own stop — the one thing the resume watchdog must respect.
    userPausedRef.current = shownIsPlaying;
    if (!followerMode) {
      handlePlayPause();
      return;
    }
    if (primaryIsPlaying) requestPause();
    else requestPlay();
  };

  // Origin "video" marks this as "a video tile moved the cursor", which is what
  // makes the primary's seek detector apply it inside the clip it already holds.
  const seekSharedCursor = (timestamp: number) => setSelectedTime(timestamp, "video");

  const handleTransportSkip = (deltaSeconds: number) => {
    if (!followerMode) {
      if (deltaSeconds < 0) handleSkipBack();
      else handleSkipForward();
      return;
    }
    const st = cursorStore.getSnapshot().selectedTime;
    if (st == null) return;
    seekSharedCursor(st + deltaSeconds * 1000);
  };

  // Committed scrub. The follower still moves its OWN element first (no lag on
  // the tile the user is dragging) and then publishes the equivalent absolute
  // timestamp — the same clip-relative -> absolute math the primary's RAF loop
  // uses to write the cursor.
  const handleTransportSeekComplete = (value: number[]) => {
    handleSeekComplete(value);
    if (!followerMode) return;
    const cv = currentVideoRef.current;
    if (cv) seekSharedCursor(computeAbsoluteTimestamp(cv.start_time, value[0]));
  };

  const handleTransportCycleRate = () => {
    const speeds = [1, 1.5, 2, 2.5, 3];
    const currentIndex = speeds.indexOf(shownPlaybackRate);
    const nextRate = speeds[(currentIndex + 1) % speeds.length];
    if (followerMode) setVideoPlaybackRate(nextRate);
    else handlePlaybackRateChange(nextRate);
  };

  return (
    <div className={`w-full h-full flex flex-col ${followerMode ? '' : 'rounded-xl'}`}>
      {/* Video Display */}
      <div className={`flex-1 flex justify-center items-center bg-background overflow-hidden ${followerMode ? '' : 'border rounded-t-xl'}`}>
        {isLoading || isDataChanging ? (
          <div className="flex flex-col items-center space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                {isDataChanging ? t("video.updating") : t("video.loading")}
              </p>
              <div className="w-32">
                <Progress value={isDataChanging ? 50 : 75} className="h-1" />
              </div>
            </div>
          </div>
        ) : currentVideo ? (
          <div className="w-full h-full bg-black/95 dark:bg-black flex items-center justify-center relative">
            <video
              ref={videoRef}
              width="100%"
              height="100%"
              className="object-contain max-w-full max-h-full"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              // Followers never render `currentTime` (no scrubber/overlay/timestamp —
              // all primary-only), so skip its ~4×/sec state update for them: it would
              // re-render the whole follower tile for nothing. The primary keeps it for
              // its live timestamp + scrubber.
              onTimeUpdate={followerMode ? undefined : handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadataAndPrime}
              onEnded={handleEnded}
              onWaiting={handleVideoStalledAtEdge}
              onStalled={handleVideoStalledAtEdge}
            >
              {t("alertDetail.videoUnsupported")}
            </video>

            {/* Video Navigation Buttons (primary only) */}
            {!followerMode && sortedVideos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrevVideo}
                  disabled={currentVideoIndex <= 0}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 bg-black/70 hover:bg-black/90 text-white border border-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t("video.previous")}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNextVideo}
                  disabled={currentVideoIndex >= sortedVideos.length - 1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 bg-black/70 hover:bg-black/90 text-white border border-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t("video.next")}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>

                {/* Video Timestamp */}
                <div className="absolute top-2 left-2 z-10 bg-black/70 text-white text-xs px-2 py-1 rounded border border-white/20">
                  {currentVideo && (() => {
                    const currentTimestamp = new Date(computeAbsoluteTimestamp(currentVideo.start_time, currentTime));
                    // Org-local time (CSN / org 6 → US Central), DST-correct.
                    return formatOrgTime(currentTimestamp, "MMM D, hh:mm:ss A");
                  })()}
                </div>
              </>
            )}

            {/* Fullscreen lives ON the video wherever the bar below does not
                carry it: follower tiles with no bar at all (extra camera
                angles), and every map tile, whose bar is trimmed to just the
                scrubber. Where the full bar is present it already has the
                button, and a second one would duplicate the affordance. */}
            {((followerMode && !sharedTransport) || minimalControls) && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFullscreen}
                className="absolute right-2 top-2 z-10 h-8 w-8 bg-black/70 hover:bg-black/90 text-white border border-white/20"
                title={t("video.fullscreen")}
              >
                <Maximize className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("video.empty")}</p>
        )}
      </div>

      {/* Controls bar. The primary always has it; a follower gets the SAME bar
          when the tile opted into `sharedTransport` (the map's per-machine
          stack), wired to the shared playback rather than to its own element. */}
      {(!followerMode || sharedTransport) && currentVideo && (
        <div
          className={cn(
            "bg-card/95 backdrop-blur-sm rounded-b-xl border-t border",
            // The map trims the bar to the scrubber alone, so it stays a thin
            // line under the picture instead of a control panel competing with
            // it. Fullscreen moved onto the video above; the transport is the
            // shared timeline cursor, not this tile.
            // Thin only where the bar really is just the scrubber.
            minimalControls && followerMode ? "px-3 py-1.5" : "p-3 space-y-2",
          )}
        >
          {/* Action Controls — above the scrubber */}
          {!minimalControls && (
          <div className="flex items-center justify-between gap-1">
            {/* Icon toolbar — hidden on the map (minimalControls), identically
                for the primary and its followers; the map keeps only the
                fullscreen button plus the playback row below. The toggles here
                are all primary-only concepts (this tile's cursor-write link, its
                clip rollover, its orientation), so followers never show them. */}
            {!minimalControls && !followerMode && (
            <div className="flex items-center gap-1">
              {/* Timeline Sync Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSyncWithTimeline(!syncWithTimeline)}
                className={`h-7 w-7 ${syncWithTimeline ? 'text-primary' : ''}`}
                title={syncWithTimeline ? t("video.disableSync") : t("video.enableSync")}
              >
                {syncWithTimeline ? (
                  <Link className="h-3.5 w-3.5" />
                ) : (
                  <Unlink className="h-3.5 w-3.5" />
                )}
              </Button>
              {/* Autoplay Toggle */}
              {sortedVideos.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setContinuousPlayback(!continuousPlayback)}
                  className="h-7 w-7"
                  title={continuousPlayback ? t("video.disableAutoplay") : t("video.enableAutoplay")}
                >
                  {continuousPlayback ? (
                    <Repeat className="h-3.5 w-3.5" />
                  ) : (
                    <CircleSlash className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
              {/* Center timeline on current video position */}
              <Button
                variant="ghost"
                size="icon"
                onClick={requestCenter}
                className="h-7 w-7"
                title={t("video.centerTimeline")}
              >
                <Crosshair className="h-3.5 w-3.5" />
              </Button>
              {/* Orientation Toggle */}
              {onOrientationChange && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOrientationChange(orientation === "vertical" ? "horizontal" : "vertical")}
                  className="h-7 w-7"
                  title={orientation === "vertical" ? t("video.switchHorizontalRatio") : t("video.switchVerticalRatio")}
                >
                  {orientation === "vertical" ? (
                    <RectangleHorizontal className="h-3.5 w-3.5" />
                  ) : (
                    <RectangleVertical className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
            </div>
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFullscreen}
                disabled={!currentVideo}
                className="h-7 w-7"
                title={t("video.fullscreen")}
              >
                <Maximize className="h-3.5 w-3.5" />
              </Button>
              {!minimalControls && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDownload}
                    disabled={!currentVideo}
                    className="h-7 w-7"
                    title={t("video.download")}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleShare}
                    disabled={!currentVideo}
                    className="h-7 w-7"
                    title={t("video.shareButton")}
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
          )}

          {/* Timeline Scrubber — isolated so its rAF-cadence updates don't
              re-render this parent (see VideoScrubber). `isPlaying` stays this
              tile's OWN element state (the scrubber's rAF reads that element);
              on a follower `currentTime` is sampled only where the shared cursor
              repositions the element, since followers get no `timeupdate`. */}
          <VideoScrubber
            videoRef={videoRef}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeek}
            onSeekComplete={handleTransportSeekComplete}
            formatTime={formatTime}
          />

          {/* Playback Controls — below the scrubber. Kept on the PRIMARY (the
              map's bottom tile) because it owns the clip sequence and drives the
              shared playback, so an operator has to be able to stop and step it.
              Dropped on map FOLLOWERS, which are extra angles of that same
              playback — a transport there would be a second, competing way to
              move one timeline. */}
          {(!minimalControls || !followerMode) && (
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleTransportSkip(-5)}
              disabled={!currentVideo}
              className="h-8 w-8"
            >
              <SkipBack className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleTransportPlayPause}
              disabled={!currentVideo}
              className="h-9 w-9"
              // Label only (no `title`): the icon has no accessible name, and a
              // tooltip would be a visible change on the tiles that already ship.
              aria-label={shownIsPlaying ? "Pause" : "Play"}
            >
              {shownIsPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleTransportSkip(5)}
              disabled={!currentVideo}
              className="h-8 w-8"
            >
              <SkipForward className="h-4 w-4" />
            </Button>
            {/* Playback Speed Control */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTransportCycleRate}
              disabled={!currentVideo}
              className="h-8 px-2 text-xs font-mono min-w-[3rem]"
              title={t("video.playbackSpeed")}
            >
              {shownPlaybackRate}x
            </Button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPreviewEnhanced;
